import { browser } from '$app/environment';
import dayjs from 'dayjs';

import { sentry } from '$shared/helpers/logger/sentry';
import { DateFormat, DateTimeFormat } from '$shared/constants/Date';
import { ModelException } from '$shared/exceptions/ModelException';
import type { IDateString } from '$shared/interfaces/IDateString';

const defaultLanguage = (browser && window?.navigator[0]) || 'en-us';
const localeDatetimeFormatter = new Intl.DateTimeFormat(defaultLanguage, {
  dateStyle: 'short',
  timeStyle: 'short'
});

const localeDateFormatter = new Intl.DateTimeFormat(defaultLanguage, {
  dateStyle: 'short'
});

export default class DateString implements IDateString {
  private readonly date: string;

  constructor(date: string) {
    this.date = date || '';
  }

  /**
   * Expects ISO date string input
   * Used to set the date in the HTML date picker
   * If data is invalid or null, returns empty string
   * @returns Date section from ISO string, eg: 2021-12-02
   */
  getDate(): string {
    try {
      if (!this.date) {
        return '';
      }
      return this.date.split('T')[0];
    } catch (err) {
      const exception = ModelException.from(
        'DateString',
        'getDate',
        {
          date: this.date
        },
        err,
        this
      );
      sentry.log(exception);
    }
    return '';
  }

  /**
   * Formats the date to the default app format (MM/DD/YYYY)
   * @returns Formatted date string in US format, eg: 12/02/2021
   */
  public getDefaultFormattedDate(): string {
    try {
      if (!this.date) return '';

      const date = this.getDate();
      const [year, month, day] = date.split('-');
      return `${month}/${day}/${year}`;
    } catch (err) {
      const exception = ModelException.from(
        'DateString',
        'getDefaultFormattedDate',
        { date: this.date },
        err,
        this
      );
      sentry.log(exception);
    }
    return '';
  }

  getLocalizedDate(): string {
    try {
      if (!this.date) {
        return '';
      }
      return localeDateFormatter.format(new Date(this.date));
    } catch (err) {
      const exception = ModelException.from(
        'DateString',
        'getLocalizedDate',
        {
          date: this.date
        },
        err,
        this
      );
      sentry.log(exception);
    }
    return '';
  }

  getLocalizedDateAndTime(): string {
    try {
      if (!this.date) {
        return '';
      }
      return localeDatetimeFormatter.format(new Date(this.date));
    } catch (err) {
      const exception = ModelException.from(
        'DateString',
        'getLocalizedDateAndTime',
        {
          date: this.date
        },
        err,
        this
      );
      sentry.log(exception);
    }
    return '';
  }

  getParsedDate(): string {
    try {
      if (!this.date) {
        return '';
      }
      const [year, month, date] = this.date.split('T').first().split('-');
      return `${month}/${date}/${year}`;
    } catch (err) {
      const exception = ModelException.from(
        'DateString',
        'getParsedDate',
        {
          date: this.date
        },
        err,
        this
      );
      sentry.log(exception);
    }
    return '';
  }

  getParsedDateTime(): string {
    try {
      if (!this.date) {
        return '';
      }
      return dayjs(this.date).format(DateTimeFormat.Default);
    } catch (err) {
      const exception = ModelException.from(
        'DateString',
        'getParsedDateTime',
        {
          date: this.date
        },
        err,
        {}
      );
      sentry.log(exception);
    }
    return '';
  }

  toString(): string {
    return this.getParsedDate();
  }

  toISOString(): string {
    return dayjs(this.date).toISOString();
  }

  toUsFormat(): string {
    return dayjs(this.date).format(DateFormat.Default);
  }

  getRawString(): string {
    return this.date;
  }

  static create(dateString: string | null | undefined): DateString | null {
    if (!dateString) return null;

    return new DateString(dateString);
  }
}
